<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('componentEditSmartIntego.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item">
                            <router-link :to="pathToInstallationEditComponents">{{ $t('componentEditSmartIntego.breadcrumb') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('componentEditSmartIntego.title') }}</li>
                    </ol>
                </div>
            </div>

            <div class="row pb-4">
                <div class="col-xxl-8 col-xl-10">
                    <div class="card">
                        <div class="card-header pb-0">
                            <h4 class="header-title">{{ $t('componentEditSmartIntego.section') }}</h4>
                        </div>
                        <div class="card-body">
                            <input ref="fileInput" id="smart-intego-file" type="file" accept=".csv,text/csv" class="d-none" @change="onFileSelected" />
                            <button id="smart-intego-import" type="button" class="btn btn-primary" :disabled="importing" @click="fileInput.click()">
                                <span v-if="importing" class="spinner-border spinner-border-sm me-1" role="status"></span>
                                <i v-else class="mdi mdi-file-upload-outline me-1"></i>{{ $t('componentEditSmartIntego.import') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                <router-link :to="pathToInstallationEditComponents" class="btn btn-outline-secondary">{{ $t('common.back') }}</router-link>
            </div>
        </div>
    </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useUserStore } from '@/stores/userStore';
import { showToast } from '@/services/toast';

// Smart Intego integration of a LINK: the configuration file (CSV) declares the readers
export default {
    name: 'ComponentEditSmartIntego',
    setup() {
        const userStore = useUserStore();
        const router = useRouter();
        const route = useRoute();
        const { t } = useI18n();
        const facilityId = parseInt(route.query.facilityId);
        const hubId = parseInt(route.query.hubId);
        const hub = userStore.getHub(facilityId, hubId);
        const fileInput = ref(null);
        const importing = ref(false);

        const pathToInstallationEditComponents = computed(() => `/installation-edit?id=${facilityId}&action=components`);

        // Content of the file in base64, without the data URL prefix
        const readAsBase64 = (file) => new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(file);
        });

        const onFileSelected = async (event) => {
            const file = event.target.files[0];
            // The same file can be chosen again after an error
            event.target.value = '';
            if (!file) return;

            importing.value = true;
            try {
                let csvBase64;
                try {
                    csvBase64 = await readAsBase64(file);
                } catch (error) {
                    console.error('Smart Intego file reading failed', error);
                    throw t('meca.smartIntegoImportFailed');
                }
                await userStore.importSmartIntego(facilityId, hub, csvBase64);
                // The components list reloads the LINK with its new readers
                await router.push(pathToInstallationEditComponents.value);
                showToast('success', t('common.updateSuccess'));
            } catch (message) {
                showToast('error', message);
            } finally {
                importing.value = false;
            }
        };

        return {
            fileInput,
            importing,
            onFileSelected,
            pathToInstallationEditComponents,
        };
    },
};
</script>
